/**
 * Jurisdiction & Consent Policy Catalog — KI-049 Wave B7 d1.
 *
 * The Kantera codebase has been assuming a single jurisdiction
 * (US COPPA, age 13) since launch. This module turns that assumption
 * into an explicit, typed catalog so the registration and consent
 * flows in d2 can switch on the caller's jurisdiction.
 *
 * Codes follow a short, opinionated namespace:
 *
 *   <REGION>_<FRAMEWORK>
 *
 * to avoid the ambiguity of bare two-letter country codes — a single
 * country can fall under multiple frameworks at once (US + COPPA for
 * minors and CCPA for adults; EU + GDPR with member-state opt-outs;
 * UK + UK-GDPR which diverged in 2021). 16-character upper-bound
 * keeps VARCHAR(16) on the DB side tractable (CA_PIPEDA, US_CCPA,
 * QC_LAW25 all need ≥9).
 *
 * d1 covers the four jurisdictions Taylor named at planning time. New
 * ones can land via PRs that extend `JURISDICTION_POLICIES` and add a
 * test case; the unit tests guard the shape (every entry must have
 * the required fields).
 */
/**
 * The catalog. Indexed by `JurisdictionCode` so lookups are O(1).
 * NOTE: keep this object frozen; the d2 wiring relies on the shape
 * being immutable at module-load time so a misbehaving caller can't
 * splice in a more permissive policy at runtime.
 */
export const JURISDICTION_POLICIES = Object.freeze({
    US_COPPA: {
        code: 'US_COPPA',
        name: 'United States — COPPA',
        region: 'US',
        consentAgeYears: 13,
        acceptedVerificationMethods: [
            'parent_id_check',
            'credit_card_check',
            'knowledge_quiz',
            'video_call',
            'email_plus_minus',
        ],
        consentRetentionDays: 365 * 3,
        refreshRequired: false,
        notes: 'COPPA 16 CFR Part 312. Verifiable parental consent required for children under 13. ' +
            'FTC accepts the listed methods; "email plus" is the minimum bar (§312.5(b)).',
    },
    EU_GDPR: {
        code: 'EU_GDPR',
        name: 'European Union — GDPR Art. 8',
        region: 'EU',
        consentAgeYears: 16,
        acceptedVerificationMethods: ['parent_id_check', 'video_call', 'credit_card_check'],
        consentRetentionDays: 365 * 6,
        refreshRequired: true,
        notes: 'GDPR Art. 8: "the processing of personal data of a child shall be lawful where the child ' +
            'is at least 16 years old." Member states may lower to 13. d2 will need a per-member-state ' +
            'override table — for d1 we treat 16 as the conservative default. Re-consent on material ' +
            'purpose change per Art. 7(3).',
    },
    UK_GDPR: {
        code: 'UK_GDPR',
        name: 'United Kingdom — UK GDPR',
        region: 'GB',
        consentAgeYears: 13,
        acceptedVerificationMethods: ['parent_id_check', 'video_call', 'credit_card_check'],
        consentRetentionDays: 365 * 6,
        refreshRequired: true,
        notes: 'Data Protection Act 2018 §9: UK opted for age 13 within the GDPR-derived framework. ' +
            'ICO Age-Appropriate Design Code (Sept 2021) overlays additional duties.',
    },
    CA_PIPEDA: {
        code: 'CA_PIPEDA',
        name: 'Canada — PIPEDA + Quebec L25',
        region: 'CA',
        consentAgeYears: 13,
        acceptedVerificationMethods: [
            'parent_id_check',
            'credit_card_check',
            'knowledge_quiz',
            'email_plus_minus',
        ],
        consentRetentionDays: 365 * 3,
        refreshRequired: false,
        notes: 'PIPEDA has no statutory age but the OPC guidance treats consent for children under 13 ' +
            'as requiring a parent/guardian. Quebec Law 25 (Sept 2023) tightens to age 14 — d2 will ' +
            'need a province override; for d1 we use the federal floor.',
    },
    AU_PRIV: {
        code: 'AU_PRIV',
        name: 'Australia — Privacy Act 1988',
        region: 'AU',
        consentAgeYears: 15,
        acceptedVerificationMethods: ['parent_id_check', 'credit_card_check', 'video_call'],
        consentRetentionDays: 365 * 7,
        refreshRequired: false,
        notes: 'Australian Privacy Act + APP 3.6: "young person" considered able to give consent ' +
            'around age 15. Below that, parental consent required. Online Privacy Bill (in progress) ' +
            'may shift this to 16.',
    },
});
/**
 * Default fallback when the caller hasn't supplied a jurisdiction.
 * Today every consent flow assumes COPPA, so US_COPPA is the
 * forward-compatible default — the d2 registration flow will replace
 * this with an explicit org-level setting from `clubs.jurisdiction`.
 */
export const DEFAULT_JURISDICTION = 'US_COPPA';
/**
 * The strictest consent threshold in the catalog, DERIVED from it rather than
 * written down — so adding a jurisdiction with a higher age cannot leave this
 * behind.
 *
 * This is the value to assume when a club's own threshold is not yet known.
 * Unknown is a third state, never "no consent needed": being asked for a parent
 * unnecessarily costs a moment, while not being asked registers a child without
 * consent.
 */
export const MAX_CONSENT_AGE_YEARS = Math.max(...Object.values(JURISDICTION_POLICIES).map((p) => p.consentAgeYears));
/**
 * Look up a jurisdiction policy. Throws on unknown codes — a typo
 * here should be loud, not silent (a missing jurisdiction means we
 * default-deny consent flows in d2).
 */
export function getJurisdictionPolicy(code) {
    const policy = JURISDICTION_POLICIES[code];
    if (!policy) {
        throw new Error(`Unknown jurisdiction code: ${String(code)}`);
    }
    return policy;
}
/**
 * Convenience: does this jurisdiction require parental consent for an
 * age in years? Returns false for callers older than the threshold.
 */
export function requiresParentalConsent(code, ageYears) {
    return ageYears < getJurisdictionPolicy(code).consentAgeYears;
}
/**
 * Type-narrowing helper for inbound API payloads. Returns true iff
 * `value` is a known jurisdiction code.
 */
export function isJurisdictionCode(value) {
    return typeof value === 'string' && value in JURISDICTION_POLICIES;
}
//# sourceMappingURL=jurisdiction.js.map